import "@patron/patron-css/patron/index.css";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Patron UI components
import { Label } from "@patron/patron-react/label";
import { Button } from "@patron/patron-react/button";
import { Notification } from "@patron/patron-react/notification";

// External packages
import base64 from "react-native-base64";

// Application components
import EnterpriseRegistrationForm from "./EnterpriseRegistrationForm";

// Constants and utilities
import * as serverConstants from "../../constants/ServerConstants";
import * as cacheUtil from "../../util/CacheUtility";
import * as constants from "../../constants/Constants";
import { validateAndSanitizeURL } from "../../util/SecurityUtility";

// Type definitions
import { EnterpriseDetailsPropType } from "./propTypes";

// Styles
import "./EnterpriseRegistration.scss";

const CustomerManaged = ({
  enterpriseResponse,
  showOrHideButton,
  enterpriseToken,
  registerEnterprise,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegisteringUrl, setIsRegisteringUrl] = useState(false);
  const [signupUrlData, setSignupUrlData] = useState(null);
  const [notificationState, setNotificationState] = useState({
    errorMsg: "",
    successMsg: "",
    showError: false,
    showSuccess: false,
  });
  const [formData, setFormData] = useState({
    enterpriseDisplayName: "",
  });

  useEffect(() => {
    // If we have an enterprise ID, move to step 2 and pre-fill the display name
    if (enterpriseResponse?.enterpriseId) {
      setCurrentStep(1);
      setFormData((prevData) => ({
        ...prevData,
        enterpriseDisplayName: enterpriseResponse.enterpriseDisplayName || "",
      }));
    }
  }, [enterpriseResponse]);

  useEffect(() => {
    let timeoutId = null;
    // If we have an enterprise token but no enterprise ID, it means the user has just completed signup
    if (enterpriseToken && !enterpriseResponse?.enterpriseId) {
      setCurrentStep(1);
      setNotificationState((prevState) => ({
        ...prevState,
        enterpriseDisplayName: enterpriseResponse?.enterpriseDisplayName || "",
        showSuccess: true,
        successMsg: t("signupFlowCompleted"),
      }));

      // Set timeout and store the timeout ID
      timeoutId = setTimeout(() => {
        setNotificationState((prevState) => ({
          ...prevState,
          showSuccess: false,
          successMsg: "",
        }));
      }, 3000); // 3 seconds delay

      const signupURLRecovered = cacheUtil.readFromCache(
        constants.SIGNUP_URL_CACHE_KEY
      );
      if (signupURLRecovered) {
        setSignupUrlData(JSON.parse(signupURLRecovered));
      } else {
        // Handle case where signup URL is not found in cache
        console.warn("No signup URL found in cache");
      }
    }

    // Cleanup function to clear timeout when component unmounts or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enterpriseToken, t, enterpriseResponse]);

  const createSignupUrl = () => {
    setIsRegisteringUrl(true);

    // Create minimal payload for customer managed signup URL
    const payload = {
      callbackUrl: `${window.location.origin}/config/enterprise`,
    };

    // Build request body for the API call
    /** @type {import('./models').SignupURLReq} */
    const body = JSON.stringify({
      path_param: "signupUrls",
      target: "mdm",
      requestType: "POST",
      payload: base64.encode(JSON.stringify(payload)),
    });

    // Make API call to get the signup URL
    fetch(window.location.origin + serverConstants.ADMIN_COMMAND_URL, {
      method: "POST",
      headers: {
        Authorization: cacheUtil.readFromCache(serverConstants.BFCONFIG_SID),
      },
      body: body,
    })
      .then(async (response) => {
        if (!response.ok) {
          const clonedResponse = response.clone();
          let errorData;
          try {
            errorData = await clonedResponse.json();
          } catch (e) {
            console.error("Failed to parse error response:", e);
            errorData = await response.text();
          }
          // Create an error object with additional information
          const error = new Error(
            `Request failed with status ${response.status}`
          );
          error.status = response.status;
          error.statusText = response.statusText;
          error.data = errorData;
          throw error;
        }
        return response.json();
      })
      .then((data) => {
        // Extract the redirect URL from the response
        if (data?.response) {
          /** @type {import('./models').SignupURL} */
          const signupURLData = JSON.parse(base64.decode(data.response));
          setSignupUrlData(signupURLData);
          setCurrentStep(1);
          setIsRegisteringUrl(false);
          cacheUtil.storeToCache(
            constants.SIGNUP_URL_CACHE_KEY,
            JSON.stringify(signupURLData)
          );
          // Redirect the user to Google's sign-in page
          if (signupURLData.url) {
            // Validate and sanitize the URL before redirecting
            // Allow Google domains for enterprise signup
            const validatedUrl = validateAndSanitizeURL(signupURLData.url, {
              allowedDomains: serverConstants.GOOGLE_ALLOWED_DOMAINS
            });
            
            if (validatedUrl) {
              window.location.href = validatedUrl;
            } else {
              console.error('URL validation failed for:', signupURLData.url);
              throw new Error('Unable to proceed with authentication. Please contact support.');
            }
          } else {
            throw new Error(
              `No signup URL found ${JSON.stringify(signupURLData)}`
            );
          }
        } else {
          throw new Error("Invalid response format");
        }
      })
      .catch((error) => {
        console.error("Error registering signup URL:", error);
        setIsRegisteringUrl(false);
        createErrNotification(error);
      });
  };

  const createErrNotification = (error) => {
    // Create a more detailed error message based on available information
        let errorMessage = t("errorRegisteringSignupUrl");

        if (error.status) {
          errorMessage += `: ${error.status}`;
          if (error.statusText) {
            errorMessage += ` - ${error.statusText}`;
          }
        }
        // Add error details from the response body if available
        if (error.data) {
          const detailsStr =
            typeof error.data === "object"
              ? error.data?.error
              : String(error.data);
          errorMessage += ` (${detailsStr})`;
        }

        setNotificationState({
          errorMsg: errorMessage,
          showError: true,
        });
  }

  // Handle form data changes from the shared form
  const handleFormDataChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  // Handle form submission
  const handleFormSubmit = (method, formData) => {
    // Add customer managed specific fields
    const submissionData = {
      ...formData,
      enterprise_token: enterpriseToken,
      enterprise_name: constants.CUSTOMER_MANAGED_TYPE,
      signupurl_name: signupUrlData?.name || "",
    };

    // Call the registerEnterprise function from props
    if (registerEnterprise) {
      registerEnterprise(method, submissionData);
    }
    cacheUtil.clearCacheItem(constants.SIGNUP_URL_CACHE_KEY);
  };

  return (
    <div className="mt-4">
      <div className="parameters-section p-4">
        <div className="hcl-row mb-3">
          <div className="hcl-col-12">
            {/* Step indicator row */}
            {enterpriseResponse?.enterpriseId ? (
              <div className="customer-managed-header">
                <div className="info-box p-3 mb-4">
                  <Label>{t("cmeUpdateInfo")}</Label>
                </div>
              </div>
            ) : (
              <div className="step-indicator mb-4">
                <div
                  className={`step ${
                    currentStep === 0 && !enterpriseToken ? "active" : "success"
                  }`}
                >
                  <span className="step-number">1</span>
                  {enterpriseToken ? (
                    <span className="step-label">{t("signupURLCreated")}</span>
                  ) : (
                    <span className="step-label">
                      {t("registeringSignupUrl")}
                    </span>
                  )}
                </div>
                <div className="step-connector"></div>
                <div className={`step ${currentStep === 1 ? "active" : ""}`}>
                  <span className="step-number">2</span>
                  <span className="step-label">
                    {t("registerEnterpriseAccount")}
                  </span>
                </div>
              </div>
            )}
            {/* Display notifications */}
            {notificationState.showError && (
              <Notification
                onClose={() =>
                  setNotificationState((prevState) => ({
                    ...prevState,
                    showError: false,
                    showSuccess: false,
                  }))
                }
                title={notificationState.errorMsg}
                type="danger"
                visible={notificationState.showError}
              />
            )}

            {notificationState.showSuccess && (
              <Notification
                onClose={() =>
                  setNotificationState((prevState) => ({
                    ...prevState,
                    showError: false,
                    showSuccess: false,
                  }))
                }
                title={notificationState.successMsg}
                type="success"
                visible={notificationState.showSuccess}
              />
            )}

            {/* Step content */}
            {currentStep === 0 && (
              <div>
                <div className="hcl-row mt-5 mb-3">
                  <div className="hcl-col-12">
                    <div className="warning-box p-3">
                      <Label>{t("noteBindButton")}</Label>
                      <Label>{t("noteCustomerSignInPage")}</Label>
                      <Label>{t("noteLoginProperly")}</Label>
                      <Label>{t("doNotRefresh")}</Label>
                    </div>
                  </div>
                </div>
                <div className="hcl-row mt-4">
                  <div className="hcl-col-12 register-button">
                    <Button
                      type="primary"
                      disabled={
                        isRegisteringUrl ||
                        enterpriseToken ||
                        showOrHideButton ||
                        !enterpriseResponse?.hasToken
                      }
                      onClick={createSignupUrl}
                    >
                      {isRegisteringUrl ? t("binding") : t("bind")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 - Show form for customer managed registration */}
            {currentStep === 1 && (
              <div className="hcl-row mt-4">
                <div className="hcl-col-12">
                  {!enterpriseToken && !enterpriseResponse?.enterpriseId && (
                    <div className="warning-box p-3 mb-4">
                      <Label>{t("waitingForEnterpriseToken")}</Label>
                    </div>
                  )}

                  {/* Use shared registration form */}
                  <EnterpriseRegistrationForm
                    enterpriseResponse={enterpriseResponse}
                    initialFormData={formData}
                    registrationType={constants.CUSTOMER_MANAGED_TYPE}
                    showSpinner={false}
                    showOrHideButton={showOrHideButton}
                    enterpriseToken={enterpriseToken}
                    signupUrlName={signupUrlData?.name}
                    onFormDataChange={handleFormDataChange}
                    onSubmit={handleFormSubmit}
                    showTermsCheckbox={false} // No terms checkbox for customer managed
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CustomerManaged.propTypes = {
  enterpriseResponse: PropTypes.shape({
    enterpriseId: PropTypes.string.isRequired,
    hasToken: PropTypes.bool,
    serviceAccount: PropTypes.string,
    accountType: PropTypes.string,
    enterpriseDetails: PropTypes.oneOfType([
      PropTypes.string,
      EnterpriseDetailsPropType,
    ]),
    enterpriseDisplayName: PropTypes.string,
    enterpriseType: PropTypes.string,
  }),
  showOrHideButton: PropTypes.bool,
  enterpriseToken: PropTypes.string,
  registerEnterprise: PropTypes.func,
};

export default CustomerManaged;
